<template>

	<anchor-container use-dot-nav>
		<template slot-scope="anchorData">
			<Layout mastheadBackground="pd/100001/global_loop1">

				<template slot="preHeadline">StudySync<mark>Global</mark></template>
				<template slot="headline">A rigorous global english language arts curriculum built for teachers everywhere.</template>

				<template slot="nav">
					<SectionNav>
						<template slot="navTitle">StudySync<mark>Global</mark></template>
						<template slot="navList">
							<ul>
								<li v-for="(component, key) in anchorData.anchors" :key="key">
									<anchor-link :to="key">{{component.title}}</anchor-link>
								</li>
							</ul>
						</template>
					</SectionNav>
				</template>

				<template slot="body">

					<anchor-content el="section" anchor="curriculum" title="Curriculum">
						<SectionHeadline>
							<template slot="subHeadline">Best-in-Class</template>
							<template slot="headline">A global curriculum for grades 6-12.</template>
							<template slot="content">
								Hybrid. Online. In-person. Built for teachers everywhere, StudySync’s new Global ELA is the only curriculum that is designed for <strong>International Middle and High Schools</strong>. The same award-winning and standards-aligned content, flexible delivery, and curricular rigor of our US edition, is now globalized, rebuilt, and supported for you and your students. Join over 2 million StudySync teachers and students moving learning forward every day, everywhere.
							</template>
						</SectionHeadline>

						<ImageTextCta>
							<template slot="image">
								<BackgroundVideo src="pd/100001/desktop_library" />
							</template>
							<template slot="entrys">
								<CheckList>
									<template slot="listItems">
										<li>
											<h3>Streamlined Curriculum</h3>
											<p>With guidance from international school teachers, our global curriculum is designed to provide a variety of culturally appropriate texts that fit with your pace of instruction while covering all the Common Core State Standards.</p>
										</li>
										<li>
											<h3>Integrated Reading & Writing</h3>
											<p>StudySync’s unique blend of contemporary and classic literature comes together with the program’s rigorous reading routines to dynamically instruct students toward mastery.</p>
										</li>
									</template>
								</CheckList>
							</template>
						</ImageTextCta>
						<ImageTextCta class="reversed">
							<template slot="entrys">
								<CheckList>
									<template slot="listItems">
										<li>
											<h3>Embedded Skills Lessons</h3>
											<p>Skill lessons ensure students build foundational language and comprehension skills, as well as reading, writing, and research on inquiry skills every day.</p>
										</li>
										<li>
											<h3>English Language Learners (ELL)</h3>
											<p>Resources are organized around a first read, a close read, and an associated skill lesson. ELL skill lessons emphasize explicit vocabulary instruction, language acquisition, and reading comprehension.</p>
										</li>
										<li>
											<h3>The Writing Process</h3>
											<p>Writing and Grammar Projects guide students through the process of writing multi-paragraph essays in a variety of forms. Explicit writing instruction through a combination of Writing Process and Skill lessons, guide students through the stages of Planning, Drafting, Revising, Editing, and Publishing.</p>
										</li>
										<li>
											<h3>Assessments</h3>
											<p>Self, peer, and teacher evaluations are embedded in every unit lesson to provide teachers a wide variety of short and longer cycle formative assessment options. End-of-unit summative assessments combine progress monitoring tools and targeted lessons for re-teaching and remediation to meet the needs of all learners.</p>
										</li>
									</template>
								</CheckList>
							</template>
							<template slot="image">
								<img src="~img/global/2.png">
							</template>
						</ImageTextCta>
						<ImageTextCta>
							<template slot="image"><img src="~img/global/3.png"></template>
							<template slot="entrys">
								<CheckList>
									<template slot="listItems">
										<li>
											<h3>Peer Review</h3>
											<p>Our peer review platform creates a community of writers giving students an authentic audience to provide immediate feedback every time they write. Anonymous peer review helps build students into skilled writers and critical thinkers.</p>
										</li>
										<li>
											<h3>StudySync's Ever-Growing, Ever-Enhanced, Responsive Platform</h3>
											<p>Technology is ever-evolving. No one downloads an app or computer program without having access to improvements and updates. It’s the same with StudySync. As new opportunities for better instruction, assessment, and student collaboration become available, we offer them directly to you, like our recent Google Classroom integration.</p>
										</li>
									</template>
								</CheckList>
							</template>
						</ImageTextCta>
					</anchor-content>

					<anchor-content el="section" anchor="planning" title="Planning">
						<SectionHeadline class="centered">
							<template slot="subHeadline">Planning</template>
							<template slot="headline">Driving Student Progress</template>
							<template slot="content">StudySync’s click ‘n play platform gives you the lesson sequence to drive student progress with additional options to tailor instruction for individual student needs. These are easy-to-use resources that help you plan at a moment’s notice.</template>
						</SectionHeadline>
						<ImageTextCta>
							<template slot="image"><img src="~img/global/4.png"></template>
							<template slot="entrys">
								<h3>Day-by-day lesson plans</h3>
								<p>Cover thematically-linked texts from a variety of genres with explicit lesson instructions and differentiation strategies. Lessons can be adjusted to fit the mode and goals of each day, or you can create your own lessons and units.</p>
							</template>
						</ImageTextCta>
						<ImageTextCta class="reversed">
							<template slot="image"><img src="~img/global/5.png"></template>
							<template slot="entrys">
								<h3>Choose your content delivery</h3>
								<p>With content that can be accessed seamlessly across digital, print, and a student mobile app, our flexible delivery options let you choose what works best for you and your students.</p>
							</template>
						</ImageTextCta>
						<ImageTextCta>
							<template slot="image"><img src="~img/global/6.png"></template>
							<template slot="entrys">
								<h3>Flexible on or offline grouping suggestions</h3>
								<p>With grouping suggestions in every lesson plan, you can choose the setup to help students both collaborate and practice independently, while staying true to your teaching style.</p>
							</template>
						</ImageTextCta>
					</anchor-content>

					<anchor-content el="section" anchor="media" title="Media">
						<Parallax v-if="!$mq.mobile" :src="require('@/assets/img/ela/class-room-recording3.jpg')" class="parallax-banner" />
						<SectionHeadline class="centered">
							<template slot="subHeadline">Captivate with Media</template>
							<template slot="headline">Introducing StudySyncTv and More</template>
							<template slot="content">Award-winning, original multimedia is carefully designed for peer-to-peer instruction to help students visualize and learn concepts from other young adults.</template>
						</SectionHeadline>
						<ImageTextCta>
							<template slot="image"><img src="~img/ela/monkey.jpg"></template>
							<template slot="action">
								<button @click.prevent="$store.commit('app/setActiveVideoModal', { src: 'lesson/364/sync_tv', cc: true })"><img src="~img/play.png"></button>
							</template>
							<template slot="entrys">
								<h3>StudySyncTV</h3>
								<p>Students watch English-speaking peers participate in person or online in a collaborative discussion to help critically analyze a text. These episodes show students how to communicate in a critical discussion of literature, how to support their viewpoint with text evidence, and how to skillfully use academic vocabulary – all while collaborating with their peers.</p>
							</template>
						</ImageTextCta>
						<ImageTextCta class="reversed">
							<template slot="image"><img src="~img/ela/sync-skills2.jpg"></template>
							<template slot="action">
								<button @click.prevent="$store.commit('app/setActiveVideoModal', { src: 'define/1650/video', cc: true })"><img src="~img/play.png"></button>
							</template>
							<template slot="entrys">
								<h3>SkillsTV</h3>
								<p>SkillsTV utilizes English-speaking young adults to model a conversational tone as they describe new key literary skills, such as plot, theme, character development, and tone, among others. These videos also include concept definitions, where young adults define and break down the key components of a skill or strategy.</p>
							</template>
						</ImageTextCta>
						<ImageTextCta>
							<template slot="image"><img src="~img/ela/communication.jpg"></template>
							<template slot="action">
								<button @click.prevent="$store.commit('app/setActiveVideoModal', { src: 'unit/830/preview', cc: true })"><img src="~img/play.png"></button>
							</template>
							<template slot="entrys">
								<h3>StudySync Previews</h3>
								<p>Previews build background information and promote inquiry at the start of each engaging, thematic unit of instruction. Intriguing images combine with music to set the scene for the worlds and stories that will captivate your students’ imaginations and drive their thinking about essential questions that connect the literature to their own lives and the world around them.</p>
							</template>
						</ImageTextCta>
					</anchor-content>

					<anchor-content el="section" anchor="global-citizens" title="Global Citizens">
						<SectionHeadline class="centered">
							<template slot="subHeadline">Open windows to the world</template>
							<template slot="headline">Develop global citizens</template>
							<template slot="content">Original and curated content is embedded in the program to foster the idea of global citizenship. Providing students with a variety of voices and a balanced perspective around essential themes and questions that values the place from which they view the world.</template>
						</SectionHeadline>
						<ImageTextCta>
							<template slot="image"><img src="~img/global/7.png"></template>
							<template slot="entrys">
								<h3>Globalized Content</h3>
								<p>A blend of traditional and contemporary authors, commissioned works by young writers, and globally sourced content provides a learning experience that reaches beyond a traditional curriculum.</p>
							</template>
						</ImageTextCta>
						<ImageTextCta class="reversed">
							<template slot="image"><img src="~img/global/8.png"></template>
							<template slot="entrys">
								<h3>Lesson Resources</h3>
								<p>Lesson resources help IB teachers promote global citizenship while developing knowledge and understanding of language and literature with an intercultural focus.</p>
							</template>
						</ImageTextCta>
						<ImageTextCta>
							<template slot="image"><img src="~img/global/9.png"></template>
							<template slot="entrys">
								<h3>Blasts</h3>
								<p>Real world and contemporary global topics are addressed in short read/write assignments called Blasts. Whether the topic is the recent decline of the honeybee population, the impact of Brexit on the chocolate trade, or the relationship between social media and our social lives, Blasts provide a social learning experience that builds reading, writing and research skills on a platform mediated by teachers.</p>
							</template>
						</ImageTextCta>
						<div class="sub-section">
							<SectionHeadline class="centered">
								<template slot="subHeadline">Ever-Growing Resources</template>
								<template slot="headline">An Expansive Library</template>
								<template slot="content">The robust digital library and Unit Creator ensures IB teachers have more than enough resources to create rigorous and academically challenging units for their programme of study.</template>
							</SectionHeadline>
							<LibraryShowcase class="showcase">
								<li>
									<figure>
										<img src="~img/carousel/Anniko.jpg">
									</figure>
									<figure>
										<img src="~img/carousel/ThatDay.jpg">
									</figure>
								</li>
								<li>
									<figure>
										<img src="~img/carousel/Ark.jpg">
									</figure>
									<figure>
										<img src="~img/carousel/Volar.jpg">
									</figure>
								</li>
								<li>
									<figure>
										<img src="~img/carousel/Barrio.jpg">
									</figure>
									<figure>
										<img src="~img/carousel/Senora.jpg">
									</figure>
								</li>
								<li>
									<figure>
										<img src="~img/carousel/Beacon.jpg">
									</figure>
									<figure>
										<img src="~img/carousel/Scarlet.jpg">
									</figure>
								</li>
								<li>
									<figure>
										<img src="~img/carousel/Brave.jpg">
									</figure>
									<figure>
										<img src="~img/carousel/LostIsland.jpg">
									</figure>
								</li>
								<li>
									<figure>
										<img src="~img/carousel/DreamHouse.jpg">
									</figure>
									<figure>
										<img src="~img/carousel/Gaman.jpg">
									</figure>
								</li>
							</LibraryShowcase>
						</div>
					</anchor-content>

					<anchor-content el="section" anchor="access" title="Access">
						<SectionHeadline>
							<template slot="subHeadline">Differentiation</template>
							<template slot="headline">Reaching every Student</template>
						</SectionHeadline>
						<ImageTextCta class="image-full">
							<template slot="entrys">
								<CheckList>
									<template slot="listItems">
										<li>
											<h3>Scaffolds</h3>
											<p>StudySync is setting the standard for what differentiation looks like. Our scaffolds allow for every student in a class to work on the same assignment. We provide the appropriate support for every learner – be it language summaries or sentence frames.</p>
										</li>
										<li>
											<h3>Accessibility</h3>
											<p>StudySync is constructed to support students with disabilities, providing a high-contrast interface, screen-reader support and use of the product without a mouse, among other features.</p>
										</li>
									</template>
								</CheckList>
							</template>
							<template slot="image">
								<img src="~img/global/10.png">
							</template>
						</ImageTextCta>
					</anchor-content>

				</template>

				<template slot="footer">
					<FooterCTA />
				</template>
			</Layout>
		</template>
	</anchor-container>

</template>

<script>
	import Layout from '@/components/Products/layout'
	import MastheadVideo from '@/components/Products/MastheadVideo'
	import SectionNav from '@/components/Products/SectionNav'
	import SectionHeadline from '@/components/Products/SectionHeadline'
	import ImageTextCta from '@/components/Products/ImageTextCta'
	import CheckList from '@/components/Products/CheckList'
	import FooterCTA from '@/components/FooterCTA'
	import ShadowedImage from '@/components/ShadowedImage'
	import Parallax from '@/components/Parallax'
	import LibraryShowcase from '@/components/LibraryShowcase'
	import BackgroundVideo from '@/components/VideoServerBackgroundVideo'
	import { AnchorContainer, AnchorContent, AnchorLink } from '@/components/AnchorNav'

	export default {
		metaInfo () {
			return this.$generateMetaInfo({
				title: `StudySync Global ELA`,
				description: `Hybrid. Online. In-person. Built for teachers everywhere, StudySync's new Global ELA is the only curriculum that is designed for International Middle and High Schools. The same award-winning and standards-aligned content, flexible delivery, and curricular rigor of our US edition, is now globalized, rebuilt, and supported for you and your students. Join over 2 million StudySync teachers and students moving learning forward every day, everywhere.`,
				image: 'https://www.studysync.com/static/global.jpg'
			})
		},
		components: { Layout, MastheadVideo, SectionNav, SectionHeadline, ImageTextCta, CheckList, FooterCTA, ShadowedImage, AnchorContent, AnchorContainer, AnchorLink, Parallax, LibraryShowcase, BackgroundVideo }
	}

</script>

<style lang="scss" scoped>

	section {
		overflow:hidden;
		.overflow-visible {
			overflow:visible;
		}
	}

	.parallax-banner {
		height:70vh; background:transparent; margin-top:-5vw; margin-bottom:5vw;
	}

	.library-showcase.showcase {
		margin-top:5vw;
		padding-bottom:5vw;
	}

	.sub-section {
		margin-top:14rem;
	}

</style>



// WEBPACK FOOTER //
// src/routes/products/global.vue